import type { OutcomeDefinition } from "@repo/app-types/entities/outcome";
import { greenRedYellowResultTypeCategory } from "@repo/app-types/entities/outcome";
import { outcomeColorMap } from "@repo/ui/Charts/analyticsColors";
import type { LineData } from "./trendChart.js";

export { outcomeColorMap };

/**
 * Per outcome value, a chronological daily rate array. Modeled so that indexing
 * a missing key yields `undefined` — the outcome definition and these sparklines
 * come from separate queries that can transiently disagree (e.g. right after
 * switching playbooks), so a value's series may be absent.
 */
export type OutcomeValueRateSparklines = Record<
  string,
  (number | null)[] | undefined
>;

/**
 * Builds multi-line trend chart data from per-outcome-value sparklines and a
 * definition. Returns an empty array when there are no sparklines.
 *
 * @param valueRateSparklines Per outcome value, a chronological daily rate
 *   array (ending today; `null` marks days with no data).
 * @param outcomeDefinition The outcome definition with result categories
 * @returns Array of LineData for the TrendChart component
 *
 * @example
 * // Input: valueRateSparklines
 * // {
 * //   "win_loss:won": [0.7, 0.5, 0.6],
 * //   "win_loss:lost": [0.2, 0.4, 0.3],
 * //   "win_loss:unqualified": [0.1, 0.1, 0.1],
 * // }
 * //
 * // Output: LineData[] (one line per non-N/A outcome, data in chronological order)
 * // [
 * //   { key: "win_loss:won", label: "Won", color: "#22c55e", data: [0.7, 0.5, 0.6] },
 * //   { key: "win_loss:unqualified", label: "Unqualified", color: "#eab308", data: [0.1, 0.1, 0.1] },
 * //   { key: "win_loss:lost", label: "Lost", color: "#ef4444", data: [0.2, 0.4, 0.3] },
 * // ]
 */
export function buildOutcomeMultiLineData(
  valueRateSparklines: OutcomeValueRateSparklines | null | undefined,
  outcomeDefinition: OutcomeDefinition,
): LineData[] {
  return buildOutcomeTrendMultiLineData(valueRateSparklines, outcomeDefinition);
}

/**
 * Builds multi-line trend chart data from per-outcome-value sparklines and a
 * definition.
 *
 * @param valueRateSparklines Per outcome value, a chronological daily rate array
 * @param outcomeDefinition The outcome definition with result categories
 * @returns Array of LineData for the TrendChart component
 */
export function buildOutcomeTrendMultiLineData(
  valueRateSparklines: OutcomeValueRateSparklines | null | undefined,
  outcomeDefinition: OutcomeDefinition,
): LineData[] {
  if (!valueRateSparklines || Object.keys(valueRateSparklines).length === 0) {
    return [];
  }

  // Category order: green first, then yellow, then red
  const categoryOrder: Record<string, number> = {
    [greenRedYellowResultTypeCategory.green]: 0,
    [greenRedYellowResultTypeCategory.yellow]: 1,
    [greenRedYellowResultTypeCategory.red]: 2,
  };

  // Get outcome values (excluding N/A), sorted by category order
  const nonNaResults = outcomeDefinition.results
    .filter((r) => r.category !== greenRedYellowResultTypeCategory.gray)
    .sort(
      (a, b) =>
        (categoryOrder[a.category] ?? 3) - (categoryOrder[b.category] ?? 3),
    );

  return nonNaResults.flatMap((result) => {
    // The sparklines and the outcome definition come from separate queries, so
    // they can transiently disagree (e.g. stale sparklines right after
    // switching playbooks) — skip results with no series.
    const data = valueRateSparklines[result.value];
    if (!data) {
      return [];
    }
    return {
      key: result.value,
      label: result.label,
      color: outcomeColorMap[result.category] ?? "var(--color-gray-500)",
      data,
    };
  });
}
